/*
 * The Prize Tiers section (admin-ui.md, CAE-513): the **default prize tiers** every new Contest
 * snapshots at creation (INV-08, ADR-0005). Its own section rather than a Configuration card —
 * Configuration stays the registry-governed key editor, and this is the operator's prize surface.
 *
 * Storage is the `default_payout_matrix` configuration key, read and written over API-10, so the
 * write inherits schema validation, no-op suppression, audit (old → new) and cache invalidation
 * from the config service rather than carrying a route of its own. `config:view` sees the page;
 * `config:manage` edits it.
 */
import { useEffect, useState } from 'react';
import { Banner, Button } from '@cou/admin-ui';
import { AdminShell } from '../shell/AdminShell';
import { useAdminAuth } from '../session/admin-auth-context';
import { defaultConfigClient, type ConfigClient } from '../config/config-client';
import { PayoutMatrixEditor, PrizeCellModal } from '../prizes/PayoutMatrixEditor';
import type { PayoutMatrixDraft } from '../prizes/payout-matrix';
import type { RewardCell } from '../prizes/reward-cell';

/** The configuration key this page edits. Deliberately absent from the admin-side CONFIG_REGISTRY
 * (`config/config-registry.ts`) so the generic Configuration editor does not also render it. */
const DEFAULT_PAYOUT_MATRIX_KEY = 'default_payout_matrix';

export interface PrizeTiersScreenProps {
  /** Injected for tests; defaults to the real API-10 client. */
  readonly client?: ConfigClient;
}

interface CellAddress {
  readonly segment: string;
  readonly tier: string;
}

export function PrizeTiersScreen({ client = defaultConfigClient }: PrizeTiersScreenProps) {
  const { claims, getToken } = useAdminAuth();
  const canManage = claims?.permissions.includes('config:manage') ?? false;
  const [matrix, setMatrix] = useState<PayoutMatrixDraft | null>(null);
  const [segmentRegistry, setSegmentRegistry] = useState<Record<string, string>>({});
  const [maxPicks, setMaxPicks] = useState(6);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState<string | null>(null);
  const [cellEditor, setCellEditor] = useState<CellAddress | null>(null);

  // One GET serves the page: the matrix itself, the segment columns, and the tier row count all
  // ride the same API-10 payload.
  useEffect(() => {
    const token = getToken();
    if (token === null) {
      setLoading(false);
      setLoadError('Not signed in.');
      return;
    }
    let cancelled = false;
    void (async () => {
      const result = await client.getConfig(token);
      if (cancelled) return;
      if (result.ok) {
        const stored = result.config[DEFAULT_PAYOUT_MATRIX_KEY];
        setMatrix(
          stored && typeof stored === 'object' && !Array.isArray(stored)
            ? (JSON.parse(JSON.stringify(stored)) as PayoutMatrixDraft)
            : {},
        );
        const registry = result.config['segment_registry'];
        if (registry && typeof registry === 'object') {
          setSegmentRegistry(registry as Record<string, string>);
        }
        const defaults = result.config['default_configuration'];
        if (defaults && typeof defaults === 'object') {
          const picks = (defaults as { maxPicks?: number }).maxPicks;
          if (typeof picks === 'number' && picks > 0) setMaxPicks(picks);
        }
        setLoadError(null);
      } else {
        // An error must never render as an empty grid: "no tiers set" and "the load broke" are
        // opposite answers, and the operator would save the empty one over real defaults.
        setLoadError(`Could not load the default prize tiers (${result.code ?? result.status}).`);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, getToken]);

  function setCellReward(segment: string, tier: string, cell: RewardCell | null): void {
    setMatrix((prev) => {
      const next = JSON.parse(JSON.stringify(prev ?? {})) as PayoutMatrixDraft;
      const column = { ...(next[segment] ?? {}) };
      if (cell === null) {
        delete column[tier];
      } else {
        column[tier] = cell;
      }
      // A segment with no paying tier left carries no column: an empty object would be a
      // meaningless diff against the stored matrix and churn the audit log.
      if (Object.keys(column).length === 0) {
        delete next[segment];
      } else {
        next[segment] = column;
      }
      return next;
    });
    setCellEditor(null);
    setSaveOk(null);
  }

  async function save(): Promise<void> {
    const token = getToken();
    if (token === null || matrix === null) return;
    setSaveError(null);
    setSaveOk(null);
    const result = await client.putConfig(token, DEFAULT_PAYOUT_MATRIX_KEY, matrix);
    if (result.ok) {
      setSaveOk('Default prize tiers saved. New contests will import them at creation.');
    } else {
      setSaveError(result.message ?? `Could not save (${result.code ?? result.status}).`);
    }
  }

  return (
    <AdminShell
      title="Prize Tiers"
      subtitle="The default prize tiers every new contest imports at creation."
      actions={
        canManage ? (
          <Button onClick={() => void save()} disabled={matrix === null}>
            Save defaults
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {!canManage && (
          <Banner variant="info" title="Read only">
            Your role has config:view. Editing the defaults requires config:manage.
          </Banner>
        )}
        <Banner variant="info" title="Copied into every new contest at creation">
          These tiers snapshot onto each contest the moment it is created. Editing them here never
          changes a contest that already exists — draft or finalised alike. To adjust one contest,
          use its Prizes step.
        </Banner>
        {loadError && (
          <Banner variant="danger" role="alert">
            {loadError}
          </Banner>
        )}
        {saveError && (
          <Banner variant="danger" role="alert">
            {saveError}
          </Banner>
        )}
        {saveOk && (
          <Banner variant="success" role="status">
            {saveOk}
          </Banner>
        )}
        {loading && <p className="font-ui text-[13px] text-ink-3">Loading default prize tiers…</p>}
        {!loading && !loadError && matrix !== null && (
          <PayoutMatrixEditor
            matrix={matrix}
            segmentRegistry={segmentRegistry}
            maxPicks={maxPicks}
            eyebrow="Default prize tiers"
            title="Prize tiers by correct picks & segment"
            action={
              <span className="font-ui text-[12.5px] text-ink-4">
                {maxPicks} tiers · from default_configuration.maxPicks
              </span>
            }
            emptyLabel="+ Add prize"
            hint={
              canManage
                ? 'Each paying cell delivers one reward. Rows come from default_configuration.maxPicks. Saved changes are validated, audited and cache-invalidated (API-10).'
                : 'You have config:view access — the default tiers are read-only. config:manage is required to edit and save.'
            }
            onEditCell={canManage ? (segment, tier) => setCellEditor({ segment, tier }) : undefined}
          />
        )}
      </div>
      {cellEditor && matrix && (
        <PrizeCellModal
          segmentLabel={
            cellEditor.segment === 'default'
              ? 'Default'
              : (segmentRegistry[cellEditor.segment] ?? cellEditor.segment)
          }
          tier={cellEditor.tier}
          cell={matrix[cellEditor.segment]?.[cellEditor.tier] ?? null}
          onClose={() => setCellEditor(null)}
          onSave={(cell) => setCellReward(cellEditor.segment, cellEditor.tier, cell)}
        />
      )}
    </AdminShell>
  );
}
